import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building2, CalendarDays, Download, FileText, Loader2, Upload, UserRound, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { uploadChunked } from "../lib/upload";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle,
} from "../components/ui/drawer";

const DEPARTMENT_LABELS = { sales: "销售部", design: "设计部", engineering: "工程部" };
const DEPARTMENT_LABELS_EN = { sales: "Sales", design: "Design", engineering: "Engineering" };

const dateTime = (value, locale) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString(locale);
};

const fileSize = (value) => {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unit]}`;
};

/**
 * FBf71948367e：部门知识库首页只列上传人及资料数量、最近上传时间；
 * 上传人抽屉按洛杉矶日期分组显示文件。读写范围由后端按登录账号所属部门收口。
 *
 * 【2026-10-29 修复 admin 打不开知识库】管理员被授予 /knowledge 后仍报
 * 「当前账号没有销售、设计或工程部门知识库」——后端数据层只认三个业务部门，
 * 而 admin / ceo 属于「行政/IT」。现在页面先取 GET /knowledge/departments 拿到
 * 「我能不能跨部门」，能跨就显示部门切换器（默认全部），上传时让管理员选归属部门；
 * 普通员工拿到的 options 只有自己部门，切换器不渲染，行为与改造前一致。
 */
export default function InternalKnowledgeBase() {
  const { api, lang, canAction } = useApp();
  const zh = lang === "zh";
  const locale = zh ? "zh-CN" : "en-US";
  const canUpload = canAction("/knowledge", "upload") === true;
  const [scope, setScope] = useState(null);
  // 部门筛选："" = 全部（仅跨部门账号可选）；普通员工由服务端返回唯一部门后固定住。
  const [department, setDepartment] = useState("");
  const [uploaders, setUploaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [downloadId, setDownloadId] = useState("");
  const [uploadState, setUploadState] = useState(null);
  const fileInput = useRef(null);

  const departmentLabel = useCallback((key) => {
    if (!key) return "";
    return zh ? (DEPARTMENT_LABELS[key] || key) : (DEPARTMENT_LABELS_EN[key] || key);
  }, [zh]);

  // 部门能力探测：决定是否显示切换器、上传时是否要求选部门。
  useEffect(() => {
    let alive = true;
    api.get("/knowledge/departments")
      .then(({ data }) => {
        if (!alive) return;
        setScope(data || null);
        if (data && !data.all && data.department) setDepartment(data.department);
      })
      .catch(() => { if (alive) setScope(null); });
    return () => { alive = false; };
  }, [api]);

  const allDepartments = scope?.all === true;
  const options = useMemo(() => (scope?.options || []).filter((o) => o && o.key), [scope]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = allDepartments && department ? { department } : {};
      const { data } = await api.get("/knowledge", { params });
      setUploaders(data.uploaders || []);
    } catch (error) {
      setLoadError(error?.response?.data?.detail || (zh ? "知识库加载失败，请重试。" : "Could not load the knowledge base."));
    } finally {
      setLoading(false);
    }
  }, [api, zh, allDepartments, department]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadUploader = useCallback(async (person) => {
    setDetailLoading(true);
    setDetailError("");
    setDays([]);
    try {
      const params = allDepartments && department ? { department } : {};
      const { data } = await api.get(`/knowledge/uploaders/${encodeURIComponent(person.username)}`, { params });
      setDays(data.days || []);
    } catch (error) {
      setDetailError(error?.response?.data?.detail || (zh ? "资料列表加载失败。" : "Could not load these files."));
    } finally {
      setDetailLoading(false);
    }
  }, [api, zh, allDepartments, department]);

  useEffect(() => {
    if (selected) loadUploader(selected);
  }, [selected, loadUploader]);

  // 跨部门账号没有「自己部门」，上传前必须确定归属部门；普通员工沿用本人部门（服务端也会强制）。
  // 【2026-10-29 体验修正二】上一版在「全部」视图没选部门时把上传按钮设成 disabled，
  // 结果用户点下去毫无反应（截图反馈「点击上传资料按钮没有反应」）—— 静默禁用比报错更差。
  // 现在按钮永远可点：缺部门时自动展开右侧「归属部门」下拉并给提示，用户立刻知道下一步做什么。
  const [uploadDepartment, setUploadDepartment] = useState("");
  const [deptPickerOpen, setDeptPickerOpen] = useState(false);
  // 三级回退：下拉显式选择 > 当前筛选的部门（「全部」视图没有部门，返回空）。
  const targetDepartment = uploadDepartment || (allDepartments ? "" : (scope?.department || "")) || department;
  const uploadNeedsDepartment = allDepartments && !targetDepartment;

  // 点上传：缺部门先展开选择器，选好再点一次才会弹系统文件框。全程都有可见反应。
  // 【2026-10-29 三轮修正】用户实测「切到销售部再点上传，还是没有反应」，但代码层面
  // 文件选择框确实被触发了（Playwright 真实鼠标事件监听 filechooser 事件为 true）。
  // 也就是说：按钮动作正常，用户看不到的是**系统文件选择框**——它可能被浏览器拦在
  // 后台、弹到别的窗口后面，或在远程桌面/多显示器下弹到了不在眼前的屏幕上。
  // 那种情况下页面毫无变化，用户只能判断「点了没反应」。
  // 所以这里补一条保底反馈：点击后短暂等待，若页面一直没有拿到文件（input 仍为空），
  // 就提示「文件选择框没有出现」并给出排查方向，而不是让用户对着页面干瞪眼。
  const [pickerHint, setPickerHint] = useState(false);
  const pickerTimer = useRef(null);

  const startUpload = () => {
    if (uploadNeedsDepartment) {
      setDeptPickerOpen(true);
      toast.error(zh ? "请先选择资料要归到哪个部门" : "Choose the target department first");
      return;
    }
    setPickerHint(false);
    if (pickerTimer.current) window.clearTimeout(pickerTimer.current);
    // 3 秒足以让系统弹框出现；用户一旦选中文件 handleUpload 会先把提示清掉。
    pickerTimer.current = window.setTimeout(() => setPickerHint(true), 3000);
    fileInput.current?.click();
  };

  useEffect(() => () => {
    if (pickerTimer.current) window.clearTimeout(pickerTimer.current);
  }, []);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    // 拿到文件就说明弹框一切正常，立刻取消「没弹出」的兜底提示。
    if (pickerTimer.current) { window.clearTimeout(pickerTimer.current); pickerTimer.current = null; }
    setPickerHint(false);
    if (!files.length) return;
    if (allDepartments && !targetDepartment) {
      setDeptPickerOpen(true);
      toast.error(zh ? "请先选择资料要归到哪个部门" : "Choose the target department first");
      return;
    }
    let uploaded = 0;
    const failed = [];
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      setUploadState({ name: file.name, index: index + 1, total: files.length, pct: 0 });
      try {
        const result = await uploadChunked(api, file, {
          onProgress: ({ pct }) => setUploadState({ name: file.name, index: index + 1, total: files.length, pct }),
        });
        await api.post("/knowledge/documents", {
          fileId: result.fileId,
          ...(allDepartments ? { department: targetDepartment } : {}),
        });
        uploaded += 1;
      } catch (error) {
        failed.push(file.name);
      }
    }
    setUploadState(null);
    await loadSummary();
    if (selected) await loadUploader(selected);
    if (failed.length) {
      toast.error(zh
        ? `成功 ${uploaded} 份；失败 ${failed.length} 份：${failed.slice(0, 3).join("、")}`
        : `${uploaded} uploaded; ${failed.length} failed: ${failed.slice(0, 3).join(", ")}`);
    } else {
      toast.success(zh ? `已上传 ${uploaded} 份资料` : `Uploaded ${uploaded} file(s)`);
    }
  };

  const download = async (document) => {
    setDownloadId(document.id);
    try {
      const { data } = await api.get(`/knowledge/documents/${encodeURIComponent(document.id)}/download`, { responseType: "blob" });
      const objectUrl = URL.createObjectURL(data);
      const anchor = window.document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = document.filename || "file";
      anchor.rel = "noopener";
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      toast.error(zh ? "下载失败，请稍后重试。" : "Download failed. Please try again.");
    } finally {
      setDownloadId("");
    }
  };

  return (
    <main className="space-y-4" data-testid="internal-knowledge-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        {allDepartments ? (
          // 部门切换器：管理员默认「全部」，三个部门合并按上传人聚合；普通员工不渲染。
          <div
            role="group"
            aria-label={zh ? "按部门筛选" : "Filter by department"}
            className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card p-1"
            data-testid="knowledge-department-switch"
          >
            <Building2 className="mx-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            {[{ key: "", label: zh ? "全部" : "All" }, ...options].map((option) => {
              const active = department === option.key;
              return (
                <button
                  key={option.key || "all"}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setDepartment(option.key); setSelected(null); }}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                    active ? "bg-brand-500 text-white shadow-sm" : "text-slate-600 hover:bg-muted/60"
                  }`}
                  data-testid={`knowledge-department-${option.key || "all"}`}
                >
                  {option.key ? departmentLabel(option.key) : option.label}
                </button>
              );
            })}
          </div>
        ) : <span />}
        {canUpload && <div className="flex flex-wrap items-center gap-2">
          {allDepartments && (
            // 管理员上传时必须确定归属部门 —— 服务端对这种账号也要求 body.department，缺了直接 400。
            // value 用 targetDepartment（含「跟随当前筛选部门」的回退），受控值才能正确回显。
            // open/onOpenChange 受控：点上传时若还没选部门，这里自动展开，把「下一步」摆到用户眼前。
            <Select
              value={targetDepartment}
              onValueChange={(v) => { setUploadDepartment(v); setDeptPickerOpen(false); }}
              open={deptPickerOpen}
              onOpenChange={setDeptPickerOpen}
            >
              <SelectTrigger
                className={`h-9 w-[9.5rem] ${uploadNeedsDepartment ? "border-amber-400 ring-2 ring-amber-100" : ""}`}
                aria-label={zh ? "资料归属部门" : "Target department"}
                aria-invalid={uploadNeedsDepartment || undefined}
                data-testid="knowledge-upload-department"
              >
                <SelectValue placeholder={zh ? "选择归属部门" : "Pick department"} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.key} value={option.key}>{departmentLabel(option.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <input ref={fileInput} type="file" multiple className="sr-only" onChange={handleUpload} data-testid="knowledge-file-input" />
          {/* 按钮永远可点：缺部门时展开上面的选择器并提示，绝不做「点了没反应」的静默禁用。 */}
          <Button
            type="button"
            onClick={startUpload}
            disabled={!!uploadState}
            data-testid="knowledge-upload-button"
          >
            {uploadState ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {zh ? "上传资料" : "Upload files"}
          </Button>
        </div>}
      </header>

      {uploadState && <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm" role="status" data-testid="knowledge-upload-progress">
        {zh ? `正在上传 ${uploadState.index}/${uploadState.total}：${uploadState.name} · ${Math.round(uploadState.pct || 0)}%`
          : `Uploading ${uploadState.index}/${uploadState.total}: ${uploadState.name} · ${Math.round(uploadState.pct || 0)}%`}
      </div>}

      {/* 兜底反馈：点上传后文件选择框迟迟没出现，明确告诉用户发生了什么以及怎么办。
          没有这一条时，弹框被浏览器挡掉就完全等同「按钮坏了」。 */}
      {pickerHint && !uploadState && <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        role="status"
        data-testid="knowledge-picker-hint"
      >
        <span>{zh
          ? "没有看到文件选择框？可能被浏览器拦在后台或弹到了其他窗口 —— 请检查任务栏，或再点一次「上传资料」。"
          : "No file dialog appeared? It may be blocked or opened behind another window — check your taskbar or click Upload again."}</span>
        <Button type="button" variant="outline" size="sm" onClick={() => setPickerHint(false)}>{zh ? "知道了" : "Got it"}</Button>
      </div>}

      {loading ? (
        <div className="flex min-h-36 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground" data-testid="knowledge-loading">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />{zh ? "正在加载…" : "Loading…"}
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm" role="alert" data-testid="knowledge-error">
          <p className="text-destructive">{loadError}</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={loadSummary}>{zh ? "重试" : "Retry"}</Button>
        </div>
      ) : uploaders.length === 0 ? (
        <div className="flex min-h-48 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 text-center" data-testid="knowledge-empty">
          <FileText className="mb-2 h-8 w-8 text-muted-foreground" />
          {/* 空态文案按当前视图区分：跨部门账号看的是三个部门，不能再说「本部门」。 */}
          <p className="font-medium text-slate-700">
            {allDepartments
              ? (department ? `${departmentLabel(department)}还没有共享资料` : (zh ? "三个部门都还没有共享资料" : "No shared files in any department"))
              : (zh ? "本部门还没有共享资料" : "No shared files yet")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{zh ? "上传后会按上传人显示在这里。" : "Uploaded files will appear here under their uploader."}</p>
        </div>
      ) : (
        <section className="overflow-hidden rounded-xl border border-border bg-card" aria-label={zh ? "上传人列表" : "Uploaders"} data-testid="knowledge-uploader-list">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-b border-border bg-muted/30 px-4 py-3 text-xs font-semibold text-muted-foreground sm:grid-cols-[minmax(0,1fr)_8rem_12rem]">
            <span>{zh ? "上传人" : "Uploader"}</span><span className="text-right sm:text-left">{zh ? "资料数量" : "Files"}</span><span className="hidden sm:block">{zh ? "最后上传" : "Last uploaded"}</span>
          </div>
          <div className="divide-y divide-border">
            {uploaders.map((person) => <button
              key={person.username}
              type="button"
              onClick={() => setSelected(person)}
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 sm:grid-cols-[minmax(0,1fr)_8rem_12rem]"
              data-testid={`knowledge-uploader-${person.username}`}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700"><UserRound className="h-4 w-4" /></span>
                <span className="min-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-800">{person.name || person.username}</span>
                    {/* 「全部」视图混排三个部门，必须标出每条上传人的归属部门，否则无法判断资料口径。 */}
                    {allDepartments && person.department && (
                      <span className="shrink-0 rounded-md bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-700" data-testid={`knowledge-uploader-dept-${person.username}`}>
                        {departmentLabel(person.department)}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{person.username}</span>
                  <span className="mt-1 block text-xs text-muted-foreground sm:hidden">{dateTime(person.latestUploadAt, locale)}</span>
                </span>
              </span>
              <span className="text-right text-sm tabular-nums text-slate-700 sm:text-left">{person.uploadCount}</span>
              <span className="hidden text-sm text-muted-foreground sm:block">{dateTime(person.latestUploadAt, locale)}</span>
            </button>)}
          </div>
        </section>
      )}

      <Drawer direction="right" open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DrawerContent className="left-auto right-0 top-0 bottom-0 mt-0 h-dvh w-full max-w-2xl rounded-l-2xl rounded-r-none border-l bg-background">
          <div className="flex min-h-0 h-full flex-col">
            <DrawerHeader className="shrink-0 border-b border-border px-5 py-4 pr-14">
              <DrawerTitle>{selected?.name || selected?.username || (zh ? "上传人资料" : "Uploader files")}</DrawerTitle>
              <DrawerDescription>{selected?.username}</DrawerDescription>
            </DrawerHeader>
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="icon" aria-label={zh ? "关闭" : "Close"} className="absolute right-4 top-4 z-10 h-9 w-9">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {detailLoading ? (
                <div className="flex min-h-32 items-center justify-center text-sm text-muted-foreground" data-testid="knowledge-detail-loading"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{zh ? "正在加载…" : "Loading…"}</div>
              ) : detailError ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" role="alert" data-testid="knowledge-detail-error">{detailError}</div>
              ) : days.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="knowledge-detail-empty">{zh ? "没有资料" : "No files"}</div>
              ) : <div className="space-y-6" data-testid="knowledge-days">
                {days.map((day) => <section key={day.date} aria-labelledby={`knowledge-day-${day.date}`} data-testid={`knowledge-day-${day.date}`}>
                  <h3 id={`knowledge-day-${day.date}`} className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />{day.date}
                  </h3>
                  <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                    {(day.documents || []).map((document) => <li key={document.id} className="flex items-center justify-between gap-3 px-3 py-3">
                      <span className="flex min-w-0 items-center gap-3">
                        <FileText className="h-4 w-4 shrink-0 text-brand-600" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-slate-800">{document.filename}</span>
                          <span className="block text-xs text-muted-foreground">{dateTime(document.uploadedAt, locale)} · {fileSize(document.size)}</span>
                        </span>
                      </span>
                      <Button type="button" variant="outline" size="sm" disabled={downloadId === document.id} onClick={() => download(document)} aria-label={`${zh ? "下载" : "Download"} ${document.filename}`} data-testid={`knowledge-download-${document.id}`}>
                        {downloadId === document.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      </Button>
                    </li>)}
                  </ul>
                </section>)}
              </div>}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </main>
  );
}
